/**
 * WHAT: Reusable searchable dropdown component with type-ahead filtering.
 * WHY: Many DAMA forms require selecting from large lists (enterprise users,
 *      databases, etc.) -- this component provides a consistent, searchable
 *      selection experience across the application.
 */

import React, { useState, useEffect, useRef } from 'react';

/**
 * WHAT: Dropdown input that filters options as the user types, supporting
 *       custom display formatting and multi-field search.
 * WHY: Improves usability when selecting from hundreds of enterprise users
 *      or database entries by allowing search across name, email, ID, and title.
 */
const SearchableDropdown = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Search...',
  displayField = 'display_name',
  valueField = 'user_id',
  formatDisplay = null,
  required = false,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  // True while the user is actively editing this field. We must NOT let the
  // value->searchTerm sync effect below overwrite what the user is typing —
  // otherwise (e.g. several of these dropdowns sharing one large `options`
  // array) a parent re-render re-runs the effect and wipes the typed text,
  // making the field impossible to type into or select from.
  const isEditingRef = useRef(false);

  // Update search term when value changes (for initial load / edit). Skipped
  // while the user is editing so it never clobbers in-progress typing.
  useEffect(() => {
    if (isEditingRef.current) return;
    if (value && options.length > 0) {
      const selected = options.find(opt => opt[valueField] === value);
      if (selected) {
        if (formatDisplay) {
          setSearchTerm(formatDisplay(selected));
        } else {
          setSearchTerm(`${selected[valueField]} - ${selected[displayField]}`);
        }
      }
    } else if (!value) {
      setSearchTerm('');
    }
  }, [value, options, valueField, displayField, formatDisplay]);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = options.filter(option => {
      // Search across multiple fields
      const searchableFields = [
        option[valueField],
        option[displayField],
        option.email,
        option.job_title
      ];

      return searchableFields.some(field =>
        field && field.toString().toLowerCase().includes(searchLower)
      );
    });

    setFilteredOptions(filtered);
  }, [searchTerm, options, valueField, displayField]);

  // Handle click outside to close dropdown. Also ends editing and snaps the
  // input text back to the committed value (or empty) so stray typed text that
  // wasn't selected does not linger. Clicking an option is INSIDE dropdownRef,
  // so a real selection never triggers this reset.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        isEditingRef.current = false;
        if (value && options.length > 0) {
          const selected = options.find(opt => opt[valueField] === value);
          if (selected) {
            setSearchTerm(formatDisplay ? formatDisplay(selected) : `${selected[valueField]} - ${selected[displayField]}`);
          }
        } else if (!value) {
          setSearchTerm('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, options, valueField, displayField, formatDisplay]);

  /* WHAT: Updates the search text and clears the selection when input is emptied.
   * WHY: Typing must immediately filter options and clearing the field must also
   *      clear the underlying value so the form does not submit a stale selection. */
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    isEditingRef.current = true; // protect the typed text from the value-sync effect
    setSearchTerm(newValue);
    setShowDropdown(true);

    // If input is cleared, also clear the selection
    if (!newValue) {
      onChange('');
    }
  };

  /* WHAT: Commits the selected option, updates the display text, and closes the dropdown.
   * WHY: On selection the parent form needs the value via onChange, and the input must
   *      show a human-readable label (using formatDisplay if provided). */
  const handleOptionSelect = (option) => {
    // Editing is done: set the readable label ourselves and re-allow the
    // value-sync effect for any future external value changes.
    isEditingRef.current = false;
    onChange(option[valueField]);
    if (formatDisplay) {
      setSearchTerm(formatDisplay(option));
    } else {
      setSearchTerm(`${option[valueField]} - ${option[displayField]}`);
    }
    setShowDropdown(false);
  };

  /* WHAT: Opens the dropdown when the input receives focus.
   * WHY: Users expect to see available options as soon as they click into the field. */
  const handleInputFocus = () => {
    isEditingRef.current = true;
    setShowDropdown(true);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-control"
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />

      {/* Hidden input to hold the actual value for form validation */}
      <input
        type="hidden"
        value={value}
        required={required}
      />

      {showDropdown && filteredOptions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: '300px',
            overflowY: 'auto',
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            marginTop: '2px'
          }}
        >
          {filteredOptions.map((option, index) => (
            <div
              key={option[valueField] || index}
              onClick={() => handleOptionSelect(option)}
              style={{
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                borderBottom: index < filteredOptions.length - 1 ? '1px solid #e9ecef' : 'none',
                backgroundColor: value === option[valueField] ? '#e7f3ff' : 'white'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
              onMouseLeave={(e) => {
                if (value !== option[valueField]) {
                  e.currentTarget.style.backgroundColor = 'white';
                } else {
                  e.currentTarget.style.backgroundColor = '#e7f3ff';
                }
              }}
            >
              <div style={{ fontWeight: '600', color: '#2c3e50' }}>
                {option[valueField]}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>
                {option[displayField]} {option.job_title ? `(${option.job_title})` : ''}
              </div>
              {option.email && (
                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                  {option.email}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showDropdown && searchTerm && filteredOptions.length === 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ced4da',
            borderRadius: '0.25rem',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            marginTop: '2px',
            padding: '1rem',
            textAlign: 'center',
            color: '#6c757d'
          }}
        >
          <i className="fas fa-search me-2"></i>
          No results found for "{searchTerm}"
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
